import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Card, Image, Grid, Header, Icon, Button, Loader, Message } from 'semantic-ui-react';
import { Helmet } from 'react-helmet';
import api from '../../api';
import AgentPropertyCard from './AgentPropertyCard';
import './styles.scss';

const Agent = () => {
    const { id } = useParams();
    const [agent, setAgent] = useState(null);
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchAgentData = async () => {
            try {
                setLoading(true);
                // Agent bilgilerini ve emlaklarını getir
                const response = await api.agent.getAgentInformation(id);

                if (response.statusCode === 200 && response.result?.data) {
                    setAgent(response.result.data.agent);
                    setProperties(response.result.data.properties);
                } else {
                    setError('No real estate agent found.');
                }
            } catch (err) {
                setError('An error occurred while loading the real estate consultant information.');
                console.error('Agent data fetch error:', err);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchAgentData();
        }
    }, [id]);

    if (loading) {
        return (
            <Container style={{ paddingTop: '2rem', textAlign: 'center' }}>
                <Loader active size="large" />
                <p>Emlak danışmanı bilgileri yükleniyor...</p>
            </Container>
        );
    }

    if (error) {
        return (
            <Container style={{ paddingTop: '2rem' }}>
                <Message negative>
                    <Message.Header>Hata</Message.Header>
                    <p>{error}</p>
                </Message>
            </Container>
        );
    }

    if (!agent) {
        return (
            <Container style={{ paddingTop: '2rem' }}>
                <Message warning>
                    <Message.Header>Emlak Danışmanı Bulunamadı</Message.Header>
                    <p>Aradığınız emlak danışmanı bulunamadı.</p>
                </Message>
            </Container>
        );
    }

    return (
        <>
            <Helmet>
                <title>{agent.firstName} {agent.lastName} - Emlak Danışmanı | EasySale</title>
                <meta name="description" content={`${agent.firstName} ${agent.lastName} emlak danışmanının profil sayfası. İletişim bilgileri ve emlak portföyü.`} />
            </Helmet>

            <Container style={{ paddingTop: '2rem' }}>
                {/* Back Button */}
                <div className="back-button-container">
                    <Button
                        as={Link}
                        to="/agents"
                        icon="arrow left"
                        content="Back to Agents"
                        color="orange"
                        size="medium"
                        className="back-button"
                    />
                </div>

                {/* Agent Bilgi Kartı */}
                <Card className="agent-profile-card" fluid>
                    <Card.Content>
                        <Grid>
                            <Grid.Column width={4} mobile={16}>
                                <div className="agent-photo-container">
                                    <Image
                                        circular
                                        size="medium"
                                        src={agent.profilePicture ? `https://easyrent.s3.eu-central-1.amazonaws.com/Users/${agent.profilePicture}` : '/img/avatar.jpg'}
                                        alt={`${agent.firstName} ${agent.lastName}`}
                                    />
                                </div>
                            </Grid.Column>
                            <Grid.Column width={12} mobile={16}>
                                <div className="agent-info">
                                    <Header as="h1" className="agent-name">
                                        {agent.firstName} {agent.lastName}
                                    </Header>
                                    <Header as="h3" className="agent-title">
                                        Real estate consultant
                                    </Header>

                                    <div className="contact-info">
                                        <div className="contact-item">
                                            <Icon name="phone" color="orange" />
                                            <a href={`tel:${agent.mobilePhone}`} className="contact-link">
                                                {agent.mobilePhone}
                                            </a>
                                        </div>

                                        <div className="contact-item">
                                            <Icon name="mail" color="orange" />
                                            <a href={`mailto:${agent.email}`} className="contact-link">
                                                {agent.email}
                                            </a>
                                        </div>

                                        {agent.officePhone && (
                                            <div className="contact-item">
                                                <Icon name="phone" color="orange" />
                                                <span>Ofis: {agent.officePhone}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="agent-actions">
                                        <Button
                                            color="orange"
                                            size="large"
                                            as="a"
                                            href={`tel:${agent.mobilePhone}`}
                                            icon="phone"
                                            content="Ara"
                                        />
                                        <Button
                                            color="orange"
                                            size="large"
                                            as="a"
                                            href={`mailto:${agent.email}`}
                                            icon="mail"
                                            content="E-posta Gönder"
                                        />
                                    </div>
                                </div>
                            </Grid.Column>
                        </Grid>
                    </Card.Content>
                </Card>

                {/* Emlak Listesi */}
                <div className="properties-section">
                    <Header as="h2" className="section-title">
                        <Icon name="home" color="orange" />
                        {agent.firstName} {agent.lastName} Properties
                        <span className="property-count">({properties.length} Property)</span>
                    </Header>

                    {properties.length === 0 ? (
                        <Message info>
                            <Message.Header>No property found</Message.Header>
                            <p>There are currently no properties listed.</p>
                        </Message>
                    ) : (
                        <Grid>
                            <Grid.Row>
                                {properties.map((property) => (
                                    <Grid.Column
                                        key={property.id}
                                        width={16}
                                        computer={4}
                                        tablet={8}
                                        mobile={16}
                                    >
                                        <AgentPropertyCard property={property} />
                                    </Grid.Column>
                                ))}
                            </Grid.Row>
                        </Grid>
                    )}
                </div>
            </Container>
        </>
    );
};

export default Agent;
